import type { FeatureFlags } from '@atlaskit/editor-common/types/feature-flags';
import type { UserPreferencesProvider } from '@atlaskit/editor-common/user-preferences-provider';
import type { EditorProps } from '@atlaskit/editor-core/editor';
import type { TrackChangesPluginOptions } from '@atlaskit/editor-plugin-track-changes/track-changes-plugin-type';
import type { EventHandlers } from '@atlaskit/editor-common/EventHandlers';
import type { SyncBlockDataProviderInterface } from '@atlaskit/editor-synced-block-provider/providers/types';
import { getSyncedBlockRenderer } from '@atlaskit/editor-synced-block-renderer/get-synced-block-renderer';
import { syncedBlockPlugin } from '@atlaskit/editor-plugin-synced-block/synced-block-plugin';
import { accessibilityUtilsPlugin } from '@atlaskit/editor-plugin-accessibility-utils/accessibilityUtilsPlugin';
import { contentInsertionPlugin } from '@atlaskit/editor-plugin-content-insertion/contentInsertionPlugin';
import { batchAttributeUpdatesPlugin } from '@atlaskit/editor-plugin-batch-attribute-updates/batchAttributeUpdatesPlugin';
import { borderPlugin } from '@atlaskit/editor-plugin-border/borderPlugin';
import { featureFlagsPlugin } from '@atlaskit/editor-plugin-feature-flags/featureFlagsPlugin';
import { cardPlugin } from '@atlaskit/editor-plugin-card/cardPlugin';
import { customAutoformatPlugin } from '@atlaskit/editor-plugin-custom-autoformat/customAutoformatPlugin';
import { dataConsumerPlugin } from '@atlaskit/editor-plugin-data-consumer/dataConsumerPlugin';
import { datePlugin } from '@atlaskit/editor-plugin-date/datePlugin';
import { emojiPlugin } from '@atlaskit/editor-plugin-emoji/emojiPlugin';
import { expandPlugin } from '@atlaskit/editor-plugin-expand/plugin';
import { scrollIntoViewPlugin } from '@atlaskit/editor-plugin-scroll-into-view/scroll-into-view-plugin';
import { gridPlugin } from '@atlaskit/editor-plugin-grid/gridPlugin';
import { helpDialogPlugin } from '@atlaskit/editor-plugin-help-dialog/helpDialogPlugin';
import { insertBlockPlugin } from '@atlaskit/editor-plugin-insert-block/insert-block-plugin';
import { listPlugin } from '@atlaskit/editor-plugin-list/list-plugin';
import { mediaPlugin } from '@atlaskit/editor-plugin-media/media-plugin';
import { mediaInsertPlugin } from '@atlaskit/editor-plugin-media-insert/media-insert-plugin';
import { mentionsPlugin } from '@atlaskit/editor-plugin-mentions/mentions-plugin';
import { panelPlugin } from '@atlaskit/editor-plugin-panel/panel-plugin';
import { pasteOptionsToolbarPlugin } from '@atlaskit/editor-plugin-paste-options-toolbar/paste-options-toolbar-plugin';
import { rulePlugin } from '@atlaskit/editor-plugin-rule/rule-plugin';
import { statusPlugin } from '@atlaskit/editor-plugin-status/status-plugin';
import { tablePlugin } from '@atlaskit/editor-plugin-table/table-plugin';
import { pastePlugin } from '@atlaskit/editor-plugin-paste/paste-plugin';
import { betterTypeHistoryPlugin } from '@atlaskit/editor-plugin-better-type-history/betterTypeHistoryPlugin';
import { clipboardPlugin } from '@atlaskit/editor-plugin-clipboard/clipboardPlugin';
import { textColorPlugin } from '@atlaskit/editor-plugin-text-color/text-color-plugin';
import { toolbarListsIndentationPlugin } from '@atlaskit/editor-plugin-toolbar-lists-indentation/toolbar-lists-indentation-plugin';
import { hyperlinkPlugin } from '@atlaskit/editor-plugin-hyperlink/hyperlinkPlugin';
import { breakoutPlugin } from '@atlaskit/editor-plugin-breakout/breakoutPlugin';
import { alignmentPlugin } from '@atlaskit/editor-plugin-alignment/alignmentPlugin';
import { guidelinePlugin } from '@atlaskit/editor-plugin-guideline/guidelinePlugin';
import { layoutPlugin } from '@atlaskit/editor-plugin-layout/layout-plugin';
import { extensionPlugin } from '@atlaskit/editor-plugin-extension/extensionPlugin';
import { tasksAndDecisionsPlugin } from '@atlaskit/editor-plugin-tasks-and-decisions/tasks-and-decisions-plugin';
import { findReplacePlugin } from '@atlaskit/editor-plugin-find-replace/findReplacePlugin';
import { analyticsPlugin } from '@atlaskit/editor-plugin-analytics/analyticsPlugin';
import { focusPlugin } from '@atlaskit/editor-plugin-focus/focusPlugin';
import { fragmentPlugin } from '@atlaskit/editor-plugin-fragment/fragmentPlugin';
import { EditorPresetBuilder } from '@atlaskit/editor-common/preset/builder';
import { compositionPlugin } from '@atlaskit/editor-plugin-composition/compositionPlugin';
import { contextIdentifierPlugin } from '@atlaskit/editor-plugin-context-identifier/contextIdentifierPlugin';
import type { ScrollGutterPluginOptions } from '@atlaskit/editor-plugin-base/plugin';
import { basePlugin } from '@atlaskit/editor-plugin-base/basePlugin';
import { decorationsPlugin } from '@atlaskit/editor-plugin-decorations/decorationsPlugin';
import { typeAheadPlugin } from '@atlaskit/editor-plugin-type-ahead/type-ahead-plugin';
import { captionPlugin } from '@atlaskit/editor-plugin-caption/captionPlugin';
import { indentationPlugin } from '@atlaskit/editor-plugin-indentation/indentationPlugin';
import { localIdPlugin } from '@atlaskit/editor-plugin-local-id/local-id-plugin';
import { clearMarksOnEmptyDocPlugin } from '@atlaskit/editor-plugin-clear-marks-on-empty-doc/clearMarksOnEmptyDocPlugin';
import { selectionToolbarPlugin } from '@atlaskit/editor-plugin-selection-toolbar/selection-toolbar-plugin';
import { textFormattingPlugin } from '@atlaskit/editor-plugin-text-formatting/text-formatting-plugin';
import { quickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert/quick-insert-plugin';
import { placeholderPlugin } from '@atlaskit/editor-plugin-placeholder/placeholder-plugin';
import { copyButtonPlugin } from '@atlaskit/editor-plugin-copy-button/copyButtonPlugin';
import { unsupportedContentPlugin } from '@atlaskit/editor-plugin-unsupported-content/unsupported-content-plugin';
import { editorDisabledPlugin } from '@atlaskit/editor-plugin-editor-disabled/editorDisabledPlugin';
import { floatingToolbarPlugin } from '@atlaskit/editor-plugin-floating-toolbar/floatingToolbarPlugin';
import { toolbarPlugin } from '@atlaskit/editor-plugin-toolbar/toolbar-plugin';
import { userIntentPlugin } from '@atlaskit/editor-plugin-user-intent/user-intent-plugin';
import { uiControlRegistryPlugin } from '@atlaskit/editor-plugin-ui-control-registry/ui-control-registry-plugin';
import { selectionPlugin } from '@atlaskit/editor-plugin-selection/selection-plugin';
import { codeBlockPlugin } from '@atlaskit/editor-plugin-code-block/codeBlockPlugin';
import { codeBlockAdvancedPlugin } from '@atlaskit/editor-plugin-code-block-advanced/codeBlockAdvancedPlugin';
import { primaryToolbarPlugin } from '@atlaskit/editor-plugin-primary-toolbar/primary-toolbar-plugin';
import { blockTypePlugin } from '@atlaskit/editor-plugin-block-type/blockTypePlugin';
import { widthPlugin } from '@atlaskit/editor-plugin-width/width-plugin';
import { ufoPlugin } from '@atlaskit/editor-plugin-ufo/ufo-plugin';
import { contextPanelPlugin } from '@atlaskit/editor-plugin-context-panel/contextPanelPlugin';
import { placeholderTextPlugin } from '@atlaskit/editor-plugin-placeholder-text/placeholder-text-plugin';
import { highlightPlugin } from '@atlaskit/editor-plugin-highlight/highlightPlugin';
import { blockControlsPlugin } from '@atlaskit/editor-plugin-block-controls/blockControlsPlugin';
import { userPreferencesPlugin } from '@atlaskit/editor-plugin-user-preferences/user-preferences-plugin';
import { undoRedoPlugin } from '@atlaskit/editor-plugin-undo-redo/undo-redo-plugin';
import { historyPlugin } from '@atlaskit/editor-plugin-history/historyPlugin';
import { showDiffPlugin } from '@atlaskit/editor-plugin-show-diff/show-diff-plugin';
import { trackChangesPlugin } from '@atlaskit/editor-plugin-track-changes/track-changes-plugin';

import { aiPlugin } from '@atlassian/editor-plugin-ai/aiPlugin';
import type { createPageEditorPluginAIOptions } from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';
import { aiStreamingOrchestratorPlugin } from '@atlassian/editor-plugin-ai-streaming-orchestrator/ai-streaming-orchestrator-plugin';

import { fg } from '@confluence/feature-gating';

import { createFeatureFlagsFromProps } from '../utils/createFeatureFlagsFromProps.ts';
import { getCodeBlockFormatProvider } from '../utils/codeBlockFormatProvider.ts';

export type CreateSnippetEditorPresetOptions = {
	appearance: EditorProps['appearance'];
	media: EditorProps['media'];
	tableOptions: EditorProps['allowTables'];
	expandOptions: EditorProps['allowExpand'];
	layoutOptions: EditorProps['allowLayouts'];
	placeholder: EditorProps['placeholder'];
	emojiProvider: EditorProps['emojiProvider'];
	mentionProvider: EditorProps['mentionProvider'];
	featureFlags: EditorProps['featureFlags'];
	linking: EditorProps['linking'];
	autoformattingProvider: EditorProps['autoformattingProvider'];
	taskDecisionProvider: EditorProps['taskDecisionProvider'];
	contextIdentifierProvider: EditorProps['contextIdentifierProvider'];
	editorPluginAIOptions: ReturnType<typeof createPageEditorPluginAIOptions>;
	trackChangesPluginOptions?: TrackChangesPluginOptions;
	editorPreferencesProvider?: UserPreferencesProvider;
	syncedBlockProvider: SyncBlockDataProviderInterface;
	syncedBlockEventHandlers?: EventHandlers;
};

/**
 * Creates a preset for the snippet editor (e.g. Create Snippet modal).
 * Mirrors the smart-create preset but omits the referentiality plugin so this file can be modified independently.
 */
export function createSnippetEditorPreset(options: CreateSnippetEditorPresetOptions): any {
	const appearance = options.appearance;
	const featureFlags: FeatureFlags = createFeatureFlagsFromProps(options.featureFlags);
	const getEditorFeatureFlags = () => featureFlags;

	const cardOptions = options.linking?.smartLinks;

	return new EditorPresetBuilder()
		.add([featureFlagsPlugin, featureFlags || {}])
		.add(uiControlRegistryPlugin)
		.add(userIntentPlugin)
		.add([
			toolbarPlugin,
			{
				contextualFormattingEnabled: 'always-pinned',
			},
		])
		.add([analyticsPlugin, {}])
		.add(betterTypeHistoryPlugin)
		.add([pastePlugin, { cardOptions, sanitizePrivateContent: true, isFullPage: true }])
		.add(clipboardPlugin)
		.add(focusPlugin)
		.add(compositionPlugin)
		.add([
			contextIdentifierPlugin,
			{ contextIdentifierProvider: options.contextIdentifierProvider },
		])
		.add([
			basePlugin,
			{
				allowInlineCursorTarget: true,
				allowScrollGutter: {
					// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
					getScrollElement: () => document.querySelector('.fabric-editor-popup-scroll-parent'),
				} as ScrollGutterPluginOptions,
			},
		])
		.maybeAdd(
			localIdPlugin,
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			fg('platform_editor_adf_with_localid'),
		)
		.add([
			userPreferencesPlugin,
			{
				initialUserPreferences: { toolbarDockingPosition: 'top' },
				userPreferencesProvider: undefined,
			},
		])
		.add(decorationsPlugin)
		.add([
			typeAheadPlugin,
			{
				isMobile: false,
			},
		])
		.add([primaryToolbarPlugin, { contextualFormattingEnabled: true }])
		.add([
			blockTypePlugin,
			{
				includeBlockQuoteAsTextstyleOption: true,
				lastNodeMustBeParagraph: false,
				allowBlockType: undefined,
			},
		])
		.add(clearMarksOnEmptyDocPlugin)
		.add([
			selectionToolbarPlugin,
			{
				preferenceToolbarAboveSelection: true,
				contextualFormattingEnabled: true,
			},
		])
		.add([
			hyperlinkPlugin,
			{
				editorAppearance: appearance,
				linkPicker: options.linking?.linkPicker,
				lpLinkPicker: featureFlags.lpLinkPicker ?? false,
				onClickCallback: cardOptions?.onClickCallback,
				platform: 'web',
			},
		])
		.add([
			textFormattingPlugin,
			{
				responsiveToolbarMenu: undefined,
			},
		])
		.add(widthPlugin)
		.add([
			quickInsertPlugin,
			{
				enableElementBrowser: true,
				disableDefaultItems: false,
				headless: false,
			},
		])
		.add([placeholderPlugin, { placeholder: options.placeholder }])
		.add(unsupportedContentPlugin)
		.add(editorDisabledPlugin)
		.add(copyButtonPlugin)
		.add(floatingToolbarPlugin)
		.add([selectionPlugin, { useLongPressSelection: false }])
		.add([
			codeBlockPlugin,
			{
				allowCopyToClipboard: true,
				useLongPressSelection: false,
				allowCompositionInputOverride: false,
				formatCodeProvider: getCodeBlockFormatProvider(),
			},
		])
		.add(ufoPlugin)
		.add(dataConsumerPlugin)
		.add(accessibilityUtilsPlugin)
		.add(contentInsertionPlugin)
		.add(batchAttributeUpdatesPlugin)
		.add([breakoutPlugin, { allowBreakoutButton: appearance === 'full-page' }])
		.add(alignmentPlugin)
		.add([textColorPlugin, true])
		.add(listPlugin)
		.add(rulePlugin)
		.add([
			expandPlugin,
			{
				...(typeof options.expandOptions === 'object' ? options.expandOptions : {}),
				useLongPressSelection: false,
				appearance,
				allowInteractiveExpand: true,
			},
		])
		.add(guidelinePlugin)
		.add([gridPlugin, { shouldCalcBreakoutGridLines: true }])
		.add([
			mediaPlugin,
			{
				...options.media,
				allowLazyLoading: true,
				allowBreakoutSnapPoints: true,
				allowAdvancedToolBarOptions:
					typeof options.media?.allowAdvancedToolBarOptions !== 'undefined'
						? options.media?.allowAdvancedToolBarOptions
						: true,
				allowCommentsOnMedia: false,
				allowDropzoneDropLine: true,
				allowMediaSingleEditable: true,
				allowRemoteDimensionsFetch: true,
				allowMarkingUploadsAsIncomplete: false,
				allowImagePreview: true,
				fullWidthEnabled: appearance === 'full-width',
				editorAppearance: appearance,
				uploadErrorHandler: undefined,
				waitForMediaUpload: true,
				isCopyPasteEnabled: true,
				alignLeftOnInsert:
					typeof options.media?.alignLeftOnInsert !== 'undefined'
						? options.media?.alignLeftOnInsert
						: false,
				getEditorFeatureFlags,
			},
		])
		.add(mediaInsertPlugin)
		.add(captionPlugin)
		.add([
			mentionsPlugin,
			{
				mentionProvider: options.mentionProvider,
				sanitizePrivateContent: true,
				allowZeroWidthSpaceAfter: true,
				profilecardProvider: undefined,
			},
		])
		.add([
			emojiPlugin,
			{
				emojiProvider: options.emojiProvider,
			},
		])
		.add([
			tablePlugin,
			{
				tableOptions:
					!options.tableOptions || typeof options.tableOptions === 'boolean'
						? {}
						: options.tableOptions,
				isTableScalingEnabled: true,
				allowContextualMenu: true,
				fullWidthEnabled: appearance === 'full-width',
				wasFullWidthEnabled: undefined,
				getEditorFeatureFlags,
				isCommentEditor: false,
				isChromelessEditor: false,
			},
		])
		.add(fragmentPlugin)
		.add([
			tasksAndDecisionsPlugin,
			{
				allowNestedTasks: true,
				consumeTabs: true,
				useLongPressSelection: false,
				taskDecisionProvider: options.taskDecisionProvider,
			},
		])
		.add([
			panelPlugin,
			{
				useLongPressSelection: false,
				allowCustomPanel: true,
				allowCustomPanelEdit: true,
			},
		])
		.add([helpDialogPlugin, false])
		.add(contextPanelPlugin)
		.add([
			extensionPlugin,
			{
				breakoutEnabled: appearance === 'full-page',
				useLongPressSelection: false,
				appearance,
			},
		])
		.add([
			datePlugin,
			{
				weekStartDay: undefined,
			},
		])
		.add([placeholderTextPlugin, {}])
		.add([
			layoutPlugin,
			{
				...(typeof options.layoutOptions === 'object' ? options.layoutOptions : {}),
				useLongPressSelection: false,
				UNSAFE_allowSingleColumnLayout: undefined,
				editorAppearance: appearance,
			},
		])
		.add([
			cardPlugin,
			{
				...options.linking?.smartLinks,
				fullWidthMode: appearance === 'full-width',
				linkPicker: options.linking?.linkPicker,
				lpLinkPicker: featureFlags.lpLinkPicker ?? false,
				editorAppearance: appearance,
			},
		])
		.add([
			customAutoformatPlugin,
			{
				autoformattingProvider: options.autoformattingProvider,
			},
		])
		.add([
			statusPlugin,
			{
				menuDisabled: false,
				allowZeroWidthSpaceAfter: true,
			},
		])
		.add(indentationPlugin)
		.add(scrollIntoViewPlugin)
		.add([
			toolbarListsIndentationPlugin,
			{
				showIndentationButtons: true,
				allowHeadingAndParagraphIndentation: true,
			},
		])
		.add([
			insertBlockPlugin,
			{
				allowTables: true,
				allowExpand: true,
				horizontalRuleEnabled: true,
				tableSelectorSupported: featureFlags?.tableSelector,
				nativeStatusSupported: true,
				showElementBrowserLink: true,
				appearance,
			},
		])
		.add([
			findReplacePlugin,
			{
				takeFullWidth: false,
				twoLineEditorToolbar: !!featureFlags.twoLineEditorToolbar,
			},
		])
		.add(borderPlugin)
		.add(pasteOptionsToolbarPlugin)
		.add([aiPlugin, options.editorPluginAIOptions])
		.add(aiStreamingOrchestratorPlugin)
		.add(highlightPlugin)
		.add(codeBlockAdvancedPlugin)
		.add(blockControlsPlugin)
		.add(historyPlugin)
		.add(undoRedoPlugin)
		.add(showDiffPlugin)
		.add([
			trackChangesPlugin,
			{
				showOnToolbar: true,
				ButtonWrapper: options.trackChangesPluginOptions?.ButtonWrapper,
			},
		])
		.add([
			syncedBlockPlugin,
			{
				enableSourceCreation: false,
				syncedBlockRenderer: getSyncedBlockRenderer({
					syncBlockRendererOptions: {
						eventHandlers: options.syncedBlockEventHandlers,
						stickyHeaders: {
							show: true,
							offsetTop: 0,
						},
					},
				}),
				syncBlockDataProvider: options.syncedBlockProvider,
			},
		]);
}
